import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Text, useDisclosure } from '@chakra-ui/react';
import './LandingHeader.css';
import Icon from '../ui/Icon';
import { useAuth } from '../../contexts/AuthContext';
import { useHasPublishedNews } from '../../pages/marketing/useNews';

// Primary marketing nav. Logging in / registering are the primary actions.
const navItems = [
  { label: 'Products', href: '/products' },
  { label: 'About', href: '/about/team' },
  // Hidden until at least one news post is published.
  { label: 'News', href: '/news', newsOnly: true },
  { label: 'Contact', href: '/contact' },
];

export default function LandingHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const menuButtonRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { session, email, trialsAccess, signOut } = useAuth();
  const hasNews = useHasPublishedNews();
  const visibleNavItems = navItems.filter((item) => !item.newsOnly || hasNews);

  // When signed in, the auth buttons flip to "Sign out" so it's obvious the
  // visitor is logged in. The primary action points at whatever app they can
  // reach: the dashboard for trials users, otherwise the shop (which shows the
  // pending screen for not-yet-approved accounts).
  const isSignedIn = Boolean(session);
  const appHome = trialsAccess ? '/dashboard' : '/dashboard/shop';
  const appHomeLabel = trialsAccess ? 'Dashboard' : 'Shop';

  const handleSignOut = async () => {
    onClose();
    await signOut();
    navigate('/');
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isOpen) setOpenDropdown(null);
  }, [isOpen]);

  // While the glass menu is open: lock body scroll, close on Escape, move
  // focus into the panel and trap Tab within it, then restore focus to the
  // hamburger trigger on close (WCAG 2.4.3 / dialog focus management).
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const getFocusable = () => {
      const panel = panelRef.current;
      if (!panel) return [];
      return Array.from(
        panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
    };

    // Move focus into the menu once it's rendered/visible.
    const focusTimer = window.setTimeout(() => {
      const items = getFocusable();
      (items[0] || panelRef.current)?.focus();
    }, 0);

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = getFocusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKey);
      // Return focus to the control that opened the menu.
      menuButtonRef.current?.focus();
    };
  }, [isOpen, onClose]);

  const scrollToAnchor = (anchor) => {
    if (!anchor) return;
    if (location.pathname !== '/') {
      navigate('/');
      window.setTimeout(() => {
        const el = document.querySelector(anchor);
        el?.scrollIntoView({ behavior: 'smooth' });
      }, 75);
    } else {
      const el = document.querySelector(anchor);
      el?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (e, item) => {
    const href = item?.href ?? '';
    const isExternal = Boolean(item?.external) || /^https?:\/\//i.test(href);

    if (href.startsWith('#')) {
      e.preventDefault();
      scrollToAnchor(href);
    } else if (href.startsWith('/#')) {
      e.preventDefault();
      scrollToAnchor(href.slice(1));
    } else if (isExternal) {
      e.preventDefault();
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      e.preventDefault();
      navigate(href);
    }
    onClose();
  };

  return (
    <>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <header
        className={`landing-header ${scrolled ? 'scrolled' : ''} ${
          isOpen ? 'menu-open' : ''
        }`}
      >
        <div className="landing-header-inner">
          <Link to="/" className="landing-header-logo-link">
            <img
              src="/images/no-tag-line-maroon-tail.webp"
              alt="Qentoros"
              className="landing-header-logo landing-header-logo-full"
            />
            <img
              src="/images/qentoros-logo.png"
              alt="Qentoros"
              className="landing-header-logo landing-header-logo-short"
            />
          </Link>

          <nav className="landing-header-nav">
            {visibleNavItems.map((item) =>
              item.dropdown ? (
                <div key={item.label} className="landing-header-nav-dropdown">
                  <button
                    type="button"
                    className="landing-header-nav-link landing-header-nav-dropdown-trigger"
                    aria-expanded="false"
                    aria-haspopup="true"
                  >
                    {item.label}
                    <Icon name="expand_more" className="landing-header-dropdown-chevron" />
                  </button>
                  <div className="landing-header-dropdown-menu" role="menu">
                    {item.dropdown.map((sub) => (
                      <a
                        key={sub.href}
                        href={sub.href}
                        className="landing-header-dropdown-item"
                        role="menuitem"
                        onClick={(e) => handleNavClick(e, sub)}
                      >
                        {sub.label}
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <a
                  key={item.href}
                  href={item.href}
                  className="landing-header-nav-link"
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noopener noreferrer' : undefined}
                  onClick={(e) => handleNavClick(e, item)}
                >
                  {item.label}
                </a>
              )
            )}
          </nav>

          <div className="landing-header-actions">
            {isSignedIn ? (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(appHome)}
                  display={{ base: 'none', md: 'inline-flex' }}
                >
                  {appHomeLabel}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  color="gray.700"
                  _hover={{ bg: 'gray.100' }}
                  onClick={handleSignOut}
                  display={{ base: 'none', md: 'inline-flex' }}
                  title={email ? `Signed in as ${email}` : undefined}
                >
                  Sign out
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  color="gray.700"
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => navigate('/login')}
                  display={{ base: 'none', md: 'inline-flex' }}
                >
                  Log in
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/enroll')}
                  display={{ base: 'none', md: 'inline-flex' }}
                >
                  Register
                </Button>
              </>
            )}
            <Box
              as="button"
              ref={menuButtonRef}
              className={`landing-header-menu-button ${isOpen ? 'open' : ''}`}
              onClick={isOpen ? onClose : onOpen}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
              aria-controls="landing-mobile-menu"
            >
              <span className="landing-header-hamburger" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </Box>
          </div>
        </div>
      </header>

      {/* iOS-style frosted glass mobile menu */}
      <div
        id="landing-mobile-menu"
        className={`landing-mobile-menu ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        aria-hidden={!isOpen}
      >
        <button
          type="button"
          className="landing-mobile-menu-backdrop"
          aria-label="Close menu"
          tabIndex={isOpen ? 0 : -1}
          onClick={onClose}
        />
        <div className="landing-mobile-menu-panel" ref={panelRef} tabIndex={-1}>
          <nav className="landing-mobile-menu-nav">
            {visibleNavItems.map((item, index) =>
              item.dropdown ? (
                <div
                  key={item.label}
                  className="landing-mobile-menu-item landing-header-mobile-dropdown"
                  style={{ '--i': index }}
                >
                  <button
                    type="button"
                    className="landing-header-mobile-dropdown-trigger"
                    onClick={() =>
                      setOpenDropdown((current) =>
                        current === item.label ? null : item.label
                      )
                    }
                    aria-expanded={openDropdown === item.label}
                  >
                    {item.label}
                    <Icon
                      name="expand_more"
                      className={`landing-header-mobile-chevron ${
                        openDropdown === item.label ? 'open' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`landing-header-mobile-dropdown-items ${
                      openDropdown === item.label ? 'open' : ''
                    }`}
                  >
                    {item.dropdown.map((sub) => (
                      <a
                        key={sub.href}
                        href={sub.href}
                        className="landing-header-dropdown-item"
                        onClick={(e) => {
                          handleNavClick(e, sub);
                          setOpenDropdown(null);
                        }}
                      >
                        {sub.label}
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <a
                  key={item.href}
                  href={item.href}
                  className="landing-mobile-menu-item landing-header-nav-link"
                  style={{ '--i': index }}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noopener noreferrer' : undefined}
                  onClick={(e) => handleNavClick(e, item)}
                >
                  {item.label}
                  {item.external && (
                    <Icon name="north_east" className="landing-mobile-menu-external" />
                  )}
                </a>
              )
            )}
          </nav>
          <div className="landing-mobile-menu-actions" style={{ '--i': visibleNavItems.length }}>
            {isSignedIn ? (
              <>
                {email && (
                  <Text fontSize="sm" color="gray.600" textAlign="center" mb={1}>
                    Signed in as {email}
                  </Text>
                )}
                <Button
                  variant="primary"
                  width="100%"
                  onClick={() => {
                    onClose();
                    navigate(appHome);
                  }}
                >
                  {appHomeLabel}
                </Button>
                <Button variant="ghost" width="100%" onClick={handleSignOut}>
                  Sign out
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  width="100%"
                  onClick={() => {
                    onClose();
                    navigate('/login');
                  }}
                >
                  Log in
                </Button>
                <Button
                  variant="primary"
                  width="100%"
                  onClick={() => {
                    onClose();
                    navigate('/enroll');
                  }}
                >
                  Register
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
